import React, {ChangeEventHandler, FC, useCallback, useMemo, useRef, useState} from 'react';
import {Drawer} from 'components/Drawer';
import style from './style.module.scss';
import {Typography} from 'components/Typography';
import {Arrow} from 'components/icons/Arrow';
import {ARROW_TYPE} from 'components/icons/Arrow/constants';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {selectCities} from 'modules/locations/selectors';
import _debounce from 'lodash/debounce';
import {ICity} from 'modules/locations/models';
import Icon from 'components/Icon';

interface IProps {
    isOpen: boolean;
    setOpen?: (value: boolean) => void;
    onSelect: (cityId: ICity['id']) => void;
    checkedLocationId?: ICity['id'] | null;
}

export const CitiesModal: FC<IProps> = ({isOpen, setOpen, onSelect, checkedLocationId}) => {
    const cityList = useAppSelector(selectCities);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [inputValue, setInputValue] = useState<string>('');

    const handleCityChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (e) => {
            setOpen?.(false);
            onSelect(Number(e.target.value));
        },
        [onSelect, setOpen],
    );

    const handleSearchInput = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (e) => {
            setInputValue(e.target.value);
        },
        [setInputValue],
    );

    const handleClose = useCallback(() => {
        setOpen?.(false);
        setInputValue('');

        if (inputRef.current) {
            inputRef.current.value = '';
        }
    }, [setInputValue, inputRef, setOpen]);

    const renderCityList = useMemo(() => {
        if (!cityList) {
            return [];
        }

        return cityList
            .filter(({title}) => title.toLowerCase().includes(inputValue.toLowerCase()))
            .map((city, index) => {
                return (
                    <li
                        className={style.cityItem}
                        data-autotest-target={'header'}
                        data-autotest-target-id={'header-link-location'}
                        key={index}
                    >
                        <input
                            checked={checkedLocationId === city.id}
                            className={style.cityInput}
                            id={city.id.toString()}
                            name={city.id.toString()}
                            onChange={handleCityChange}
                            type={'radio'}
                            value={city.id}
                        />
                        <label className={style.cityLabel} htmlFor={city.id.toString()}>
                            <Typography element={'div'} variant={'h3'}>
                                {city.title}
                            </Typography>
                        </label>
                    </li>
                );
            });
    }, [cityList, inputValue, checkedLocationId, handleCityChange]);

    return (
        <Drawer isOpen={isOpen}>
            <div className={style.citiesModalHeader}>
                <button className={style.backButton} onClick={handleClose}>
                    <Arrow direction={'left'} type={ARROW_TYPE.line} />
                </button>
                <Typography className={style.citiesModalTitle} element={'div'} variant={'p'}>
                    Выбор города
                </Typography>
            </div>
            <div className={style.citiesListContainer}>
                <div className={style.citiesSearch}>
                    <input
                        autoComplete={'off'}
                        className={style.searchInput}
                        data-autotest-target={'city-popup'}
                        data-autotest-target-id={'city-popup-city-search'}
                        onChange={_debounce(handleSearchInput, 500)}
                        placeholder={'Поиск города'}
                        ref={inputRef}
                        type={'search'}
                    />
                    <div className={style.searchIcon}>
                        <Icon icon={'search'} />
                    </div>
                </div>
                <ul className={style.citiesList}>{renderCityList}</ul>
            </div>
        </Drawer>
    );
};
